import { useEffect, useMemo, useRef, useState } from "react";
import { ChatbotBuilderStyled } from "./style";
import NodeProvider from "../../ui-components/flow-builder/nodeProvider";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../state/store";
import "react-toastify/dist/ReactToastify.css";
import {
  NotificationDataState,
  userDataState,
} from "../../state/storeDataStateType";
import { notificationDataStateActions } from "../../state/notificationDataState";
import { useDispatch } from "react-redux";
import SideModal, {
  SideModalProps,
} from "../../ui-components/side-modal/sideModal";
import {
  sideModalConfigsInstagram,
  sideModalConfigsGmail,
  sideModalConfigsLivechat,
  getWhatsappSideModalConfigs,
} from "../../ui-components/modal-configs/whatsappMessageBlockConfigs";
import ComponentModal from "../../ui-components/component-modal/componentModal";
import VersionHistoryPanel from "../version-history";
import AddNodePopup from "../../ui-components/flow-builder/addNodePopup";
import { NodeConfig } from "../../utilities/flowBuilder.utility";
import { homeDataAction } from "../../state/homeState";
import {
  FirebaseService,
  PAGES,
  PartnerStoreSource,
} from "../../services/firebase/FirebaseService";
import { flowMetaStateActions } from "../../state/flowMetaState";
import { useOutside } from "../../ui-components/rich-text-editor/useOutside";
import { NodeActionActions } from "../../state/nodeActionState";
import { nodeClickDataActions } from "../../state/nodeClickDataState";
import { ErrorStateHelper } from "../../services/helpers/errorStateHelper";
import { errorDataActions } from "../../state/errorDataState";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import { isProd } from "../../config";
import { COLORS, Toaster } from "@bikdotai/bik-component-library";
import { useDetectAnalytics } from "../../ui-components/flow-builder/useDetectAnalytics";
import useKeyboardEventListener from "../../utilities/keyboardEventListner";
import { storeInfoStateActions } from "../../state/storeInfoState";
import ReactJoyride, { STATUS, Step } from "react-joyride";
import TourTooltip from "../tour-tooltip/tourTooltip";
import {
  tourSteps,
  tourStepsAnalytics,
} from "../../app/messageBlock/Constants";
import { useProductTourMeta } from "../../utilities/productTourHook";
import { useAiRouterSettings } from "../../utilities/useAiRouterSettings";
import { BikStore } from "@bikdotai/bik-models/growth/models/store";
import { getCurrentUser } from "../../services/helpers/StoreHelper";
import { CHANNELS } from "../../utilities/login.utility";
import { featureRecommendationActions } from "../../state/featureRecommendationState";
import {
  getParentTypeForFeature,
  normalizeFeatureBlockId,
} from "../../utilities/featureBlockMapping";
import { NodeSubType } from "../../models/nodeSubType";
import { useFlowScope } from "../../hooks/useFlowScope";

interface ChatbotBuilderProps {
  userData: userDataState;
  forceFetch?: boolean;
  showTour: number;
}

if (isProd) {
  Sentry.init({
    dsn: "https://dd96b51ce03e48dfbd12e1cca3bbfd1f@o1418593.ingest.sentry.io/4504872250638336",
    integrations: [new BrowserTracing()],
    tracesSampleRate: 1.0,
    beforeSend: (event) => {
      if (window.location.hostname === "localhost") {
        return null;
      }
      return event;
    },
  });
}

const ChatbotBuilder = (props: ChatbotBuilderProps) => {
  const { storeId, flowId, type, channel } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const homeState = useAppSelector((state) => state.homeState);
  const dispatcher = useDispatch();
  const notificationDataState = useAppSelector(
    (state) => state.notificationDataState
  );
  const nodeClickState = useAppSelector((state) => state.nodeClickState);
  const [showRightModal, setShowRightModal] = useState<boolean>(false);
  const [config, setConfig] = useState<NodeConfig>(new NodeConfig());
  const [index, setIndex] = useState<number>(1);
  const [newNodeData, setNewNodeData] = useState<{ [key: string]: any }>({});
  const [showLeftModal, setShowLeftModal] = useState<boolean>(false);
  const isAnalytics = useDetectAnalytics();
  const location = useLocation();
  const nodeSubtypeMapping = useAppSelector(
    (state) => state.nodeSubtypeMappingState
  );
  const { fetchAndSetAiRouterSettings } = useAiRouterSettings();
  const firebaseService = new FirebaseService(
    () => {},
    () => {}
  );
  const superRef = useRef<HTMLDivElement | null>(null);
  const nodeActions = useAppSelector((state) => state.nodeActionState);
  const flowMeta: { [key: string]: any } = useAppSelector(
    (state) => state.flowMetaState.flowMeta
  );
  const { productDomain } = useFlowScope();
  const isPrimaryStoreFlow = !productDomain;
  const whatsappSideModalConfigs = useMemo(
    () => getWhatsappSideModalConfigs(isPrimaryStoreFlow),
    [isPrimaryStoreFlow]
  );
  const [sideModalConfigs, setSideModalConfigs] = useState<{
    [key: string]: Partial<SideModalProps>;
  }>({});
  const errorStateComplete = useAppSelector((state) => state.errorDataState);
  const [toasterString, setToasterString] = useState("");
  const [showToaster, setShowToaster] = useState(false);
  const [toasterType, setToasterType] = useState<any>("info");
  const [steps, setSteps] = useState<Array<Step>>(tourSteps);
  const [runTour, setRunTour] = useState<boolean>(false);
  const [resetPort, setResetPort] = useState<number>(0);
  const [isBikIntegrated, setIsBikIntegrated] = useState<boolean>(false);
  const [isManifestStore, setIsManifestStore] = useState<boolean>(false);
  const { fetchPageTourStatus, updatePageTourStatus, productTourMeta } =
    useProductTourMeta();
  const prevTourRef = useRef(props.showTour);

  //Initializing Keyboard Event listener - This captures keyboard event and handles them accordingly.
  useKeyboardEventListener();

  useOutside(superRef, () => setShowRightModal(false));

  useEffect(() => {
    if (channel && channel.toLowerCase() === "instagram") {
      setSideModalConfigs(sideModalConfigsInstagram);
    } else if (channel && channel.toLowerCase() === "email") {
      setSideModalConfigs(sideModalConfigsGmail);
    } else if (channel && channel.toLowerCase() === "livechat") {
      setSideModalConfigs(sideModalConfigsLivechat);
    } else {
      setSideModalConfigs(whatsappSideModalConfigs);
    }
  }, [channel, whatsappSideModalConfigs]);

  useEffect(() => {
    const fetchData = async () => {
      let storedUser = JSON.parse(
        localStorage.getItem("Bik/CurrentUser") || "{}"
      );

      const authUser = await FirebaseService.fetchCurrentUser();
      const authUid = authUser?.uid ?? "";

      if (
        !Object.keys(storedUser).length ||
        storedUser.authUid !== authUid ||
        storedUser.storeId !== homeState?.storeId
      ) {
        const currentUser = await getCurrentUser(
          homeState?.storeId,
          authUser?.email ?? ""
        );

        storedUser = currentUser?.id
          ? { ...currentUser, authUid, storeId: homeState?.storeId }
          : {};
        localStorage.setItem("Bik/CurrentUser", JSON.stringify(storedUser));
      }

      if (storedUser.id) {
        dispatcher(
          homeDataAction.updateState({
            userData: { ...homeState?.userData, agentId: storedUser.id },
          })
        );
      }

      const bikStoreData: BikStore = (await firebaseService.getBikStoreData(
        storeId || ""
      )) as BikStore;

      if (bikStoreData && bikStoreData.appType === "GLOBAL") {
        // Check if Bik is integrated for Manifest store
        setIsManifestStore(true);
        const _isBikIntegrated = await getIsBikIntegrated(bikStoreData);
        if (_isBikIntegrated) {
          setIsBikIntegrated(true);
        } else {
          setIsBikIntegrated(false);
        }
      }
    };

    if (homeState?.userData?.email && homeState?.storeId) {
      fetchData();
    }
  }, [homeState?.userData?.email, homeState?.storeId]);

  const multiStoreInfoRef = useRef<any>(null);
  const [isStoreInfoFetched, setIsStoreInfoFetched] = useState<boolean>(false);

  useEffect(() => {
    if (!productDomain) return;
    const childMetaData =
      multiStoreInfoRef.current?.storeInfo?.[productDomain]?.metaData;
    const childCurrency = childMetaData?.currency;
    if (childCurrency) {
      dispatcher(storeInfoStateActions.setCurrency(childCurrency));
    }
    const childTimezone = childMetaData?.timezone;
    if (childTimezone) {
      dispatcher(storeInfoStateActions.setTimezone(childTimezone));
    }
  }, [productDomain, isStoreInfoFetched]);

  useEffect(() => {
    // get store info
    firebaseService
      .getStoreInfo(storeId || "")
      .then((data) => {
        multiStoreInfoRef.current = data?.multiStoreInfo ?? null;
        dispatcher(
          storeInfoStateActions.addStoreInfoData({
            currency: data?.currency || "INR",
            countryCode: data?.countryCode || "IND",
            timezone: data?.timezone || "",
            partnerStoreDomain:
              data?.storeIntegrationStatus?.partnerStoreDomain,
            chatbotAttributionWindow:
              data?.attributionDetails?.chatbotAttributionWindow || 0,
            productCount: data?.shopifyStats?.productCount || 0,
            isNonShopifyStore:
              (data?.partnerStore?.metaData?.source ?? "")?.toLowerCase() ===
                PartnerStoreSource.CUSTOM || false,
          })
        );
        setIsStoreInfoFetched(true);
      })
      .catch();
    firebaseService.getFlowKeywordsByFlowId(storeId!, flowId!).then((data) => {
      dispatcher(
        flowMetaStateActions.setFlowMeta({
          flowMeta: {
            [flowId!]: data,
          },
          updateFlowMeta: true,
        })
      );
      if (
        data &&
        data.compilationStatus &&
        data.compilationStatus !== "SUCCESS"
      ) {
        firebaseService
          .getCompilationReportByFlowId(storeId!, flowId!)
          .then((data) => {
            const formattedErrorState = new ErrorStateHelper().formatErrors(
              data,
              flowId!
            );
            if (data && (data.compilation_date || data.compilationDate)) {
              delete data["compilation_date"];
              delete data["compilationDate"];
            }
            dispatcher(
              errorDataActions.updateErrorState({
                ...errorStateComplete,
                errorModalOpenStatus: false,
                errors: { ...formattedErrorState.errors },
                errorsCount: formattedErrorState.errorsCount,
                warningsCount: formattedErrorState.warningsCount,
              })
            );
            if (formattedErrorState.errorsCount > 0) {
              setTimeout(() => {
                dispatcher(
                  errorDataActions.updateErrorState({
                    ...errorStateComplete,
                    errorModalOpenStatus: formattedErrorState.errorsCount > 0,
                    errors: { ...formattedErrorState.errors },
                    errorsCount: formattedErrorState.errorsCount,
                    warningsCount: formattedErrorState.warningsCount,
                  })
                );
              }, 2000);
            }
          });
      }
      const today = new Date();
      today.setHours(0);
      today.setMinutes(0);
      today.setSeconds(0);

      const last7Days = new Date(today.getTime());
      last7Days.setDate(last7Days.getDate() - 6);
      const startDate = last7Days.getTime();
      const endDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        23,
        59,
        59
      ).getTime();

      dispatcher(
        homeDataAction.addState({
          storeId: storeId || "",
          flowId: flowId || "",
          flowName: data?.flowName || "",
          type: type || "",
          channel: channel || "",
          userData: props.userData,
          compilationStatus: data?.compilationStatus,
          status: data?.status,
          sunset: data?.isSunset,
          testMode: data?.isTestMode,
          lastUpdated: data?.lastUpdated || "",
          analyticsData: {
            startDate: startDate,
            endDate: endDate,
            shimmer: true,
          },
          createdAt: data?.createdAt,
          journeyType: data?.journeyType || "utility",
          dndEnabled: data?.dndEnabled,
        })
      );

      // Fetch AI Router settings AFTER addState to avoid being overwritten

      fetchAndSetAiRouterSettings(storeId || "", firebaseService);
    });
  }, [Object.keys(props?.userData)?.length]);

  useEffect(() => {
    if (
      (notificationDataState?.errorMessage || "").length ||
      (notificationDataState?.successMessage || "").length ||
      (notificationDataState?.warningMessage || "").length
    ) {
      showNotification(notificationDataState);
      dispatcher(
        notificationDataStateActions.setNotifications({
          errorMessage: "",
          successMessage: "",
          warningMessage: "",
        })
      );
    }
  }, [
    notificationDataState.errorMessage,
    notificationDataState.successMessage,
    notificationDataState.warningMessage,
  ]);

  // [Auto-open left panel - Step 3] When nodeClickState.nodeId changes (triggered by setEditNode
  // dispatch below), update config and open the left panel for the newly added block.
  useEffect(() => {
    if (nodeClickState.nodeId) {
      setConfig((prevState: NodeConfig) => {
        return {
          ...prevState,
          nodeId: nodeClickState.nodeId,
          type: nodeClickState.nodeType!,
          subType: nodeClickState.nodeSubType!,
          nodeIndex: nodeClickState.nodeIndex!,
          automationChannel: nodeClickState.automationChannel!,
        };
      });

      if (nodeClickState.hideLeftModal) {
        setShowLeftModal(false);
      } else {
        setShowLeftModal(true); // Left panel opens automatically here
      }
    }
  }, [nodeClickState.nodeId]);

  // [Auto-open left panel - Step 1] When a block is added, nodeActions fires with action="created".
  // This triggers setEditNode, which updates nodeClickState and ultimately opens the left panel.
  useEffect(() => {
    if (nodeActions?.type === "node" && nodeActions?.action === "created") {
      setNewNodeData({});

      // Check if there's a feature param to set before opening modal
      const featureParam = searchParams.get("feature");
      if (featureParam) {
        dispatcher(
          featureRecommendationActions.setFeature({
            feature: normalizeFeatureBlockId(featureParam),
          })
        );
      }

      // Remove ?feature= from URL once send_email or discount_code block is added
      const addedSubType = nodeActions.subType;
      if (
        addedSubType === NodeSubType.SEND_EMAIL ||
        addedSubType === NodeSubType.EMAIL_TEMP_NEW ||
        addedSubType === NodeSubType.DISCOUNT_CODE ||
        addedSubType === NodeSubType.AGENTIC_BUILDER
      ) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("feature");
            return next;
          },
          { replace: true }
        );
      }

      // [Auto-open left panel - Step 2] Dispatch setEditNode to update nodeClickState.
      // 100ms delay ensures the new node is fully rendered in the store before opening the panel.
      if (nodeActions.nodeId && nodeActions.nodeType && nodeActions.subType) {
        setTimeout(() => {
          // Use nodeSubtypeMapping to get the correct index (same source of truth
          // as setNodeDataFromSideModal/addNewNode). By the time "created" fires,
          // the mapping already includes the new node, so no +1 is needed.
          const nodeIndex =
            nodeSubtypeMapping.data?.[nodeActions.subType!] || 0;
          const automationChannel = homeState.channel || "";

          dispatcher(
            nodeClickDataActions.setEditNode({
              nodeId: nodeActions.nodeId!,
              nodeType: nodeActions.nodeType!,
              nodeSubType: nodeActions.subType!,
              nodeIndex: nodeIndex,
              automationChannel: automationChannel,
            })
          );
        }, 100);
      }

      dispatcher(
        NodeActionActions.updateState({
          type: "",
          action: "",
          nodeId: "",
        })
      );
    }
  }, [nodeActions]);

  // Handle feature param for new block addition (when no nodeId in URL).
  // Wait for "edit-page-loaded" (flow fully loaded) before opening the right panel,
  // matching the same timing used for /{node_id} → left panel (node.tsx useEffect([showLoader])).
  useEffect(() => {
    const featureParam = searchParams.get("feature");
    const nodeIdParam = searchParams.get("nodeId");

    if (
      featureParam &&
      !nodeIdParam &&
      !isAnalytics &&
      nodeActions?.action === "edit-page-loaded"
    ) {
      const normalizedFeature = normalizeFeatureBlockId(featureParam);
      // Dispatch feature to Redux
      dispatcher(
        featureRecommendationActions.setFeature({ feature: normalizedFeature })
      );

      // Special-case: feature=ai_agent should directly add the AI Agent block (no right panel).
      if (normalizedFeature === NodeSubType.AGENTIC_BUILDER) {
        const id = getNodeId();
        const nextIndex =
          (nodeSubtypeMapping.data?.[NodeSubType.AGENTIC_BUILDER] || 0) + 1;

        setShowRightModal(false);
        setNewNodeData({
          type: "AI",
          subType: NodeSubType.AGENTIC_BUILDER,
          nodeIndex: nextIndex,
          title: `AI Agent #${nextIndex}`,
          automationChannel: homeState.channel || "",
          id: id,
        });
        return;
      }

      // Determine the parent type for the feature and open the right modal
      const parentType = getParentTypeForFeature(normalizedFeature);
      if (parentType) {
        // First render: mount SideModal with isOpen=false (off-screen via slideOut animation).
        // Second render: set isOpen=true so slideIn animation plays as a state transition.
        // This mirrors how ComponentModal does: useState(false) → setIsModalOpen(true) on mount.
        setNewNodeData({
          type: parentType,
          subType: parentType,
        });
        setTimeout(() => {
          setShowRightModal(true);
        }, 0);
      }
    }
  }, [nodeActions, searchParams, isAnalytics]);

  const rightModalToggler = () => {
    setShowRightModal((prevState: boolean) => {
      const newState = !prevState;
      // Clear feature when closing the modal
      if (!newState) {
        dispatcher(featureRecommendationActions.clearFeature());
      }
      return newState;
    });
  };

  const closeLeftModal = () => {
    // Clear feature when left modal closes
    dispatcher(featureRecommendationActions.clearFeature());
    setShowLeftModal(false);
  };

  const addNewNode = (nodeName: string) => {
    switch (nodeName) {
      case "Condition": {
        const id = getNodeId();
        const index = (nodeSubtypeMapping.data?.[nodeName] || 0) + 1;
        setNewNodeData({
          type: nodeName,
          subType: nodeName,
          nodeIndex: index,
          title: `Condition #${index}`,
          id: id,
        });
        setShowRightModal(false);
        break;
      }
      case "Tags": {
        const id = getNodeId();
        const index = (nodeSubtypeMapping.data?.["add_tags"] || 0) + 1;
        setNewNodeData({
          type: "Action",
          subType: "add_tags",
          nodeIndex: index,
          title: `Tags #${index}`,
          id: id,
        });
        setShowRightModal(false);
        break;
      }
      case "Start flow": {
        const id = getNodeId();
        const index = (nodeSubtypeMapping.data?.["start_flow"] || 0) + 1;
        setNewNodeData({
          type: "Action",
          subType: "start_flow",
          nodeIndex: index,
          title: `Start flow #${index}`,
          id: id,
        });
        setShowRightModal(false);
        break;
      }
      case "Sticky notes": {
        const index = (nodeSubtypeMapping.data?.["sticky_notes"] || 0) + 1;
        setNewNodeData({
          type: nodeName,
          subType: "sticky_notes",
          nodeIndex: index,
          title: `Sticky notes #${index}`,
          id: `S${index}`,
        });
        setShowRightModal(false);
        break;
      }
      case "Flow control": {
        if (channel === CHANNELS.LIVECHAT) {
          setSideModalConfigs(sideModalConfigsLivechat);
        } else {
          setSideModalConfigs(sideModalConfigsInstagram);
        }
        setNewNodeData({
          type: "Condition",
        });
        setShowRightModal(true);
        break;
      }
      case "Whatsapp": {
        setSideModalConfigs(whatsappSideModalConfigs);
        setNewNodeData({
          type: "Message",
        });
        setShowRightModal(true);
        break;
      }
      case "Instagram": {
        setSideModalConfigs(sideModalConfigsInstagram);
        setNewNodeData({
          type: "Message",
        });
        setShowRightModal(true);
        break;
      }
      case "Email": {
        setSideModalConfigs(sideModalConfigsGmail);
        setNewNodeData({
          type: "Message",
        });
        setShowRightModal(true);
        break;
      }
      case "Livechat": {
        setSideModalConfigs(sideModalConfigsLivechat);
        setNewNodeData({
          type: "Message",
        });
        setShowRightModal(true);
        break;
      }
      default: {
        channel === CHANNELS.INSTAGRAM
          ? setSideModalConfigs(sideModalConfigsInstagram)
          : channel === CHANNELS.WHATSAPP
            ? setSideModalConfigs(whatsappSideModalConfigs)
            : channel === CHANNELS.EMAIL
              ? setSideModalConfigs(sideModalConfigsGmail)
              : channel === CHANNELS.LIVECHAT
                ? setSideModalConfigs(sideModalConfigsLivechat)
                : setSideModalConfigs(whatsappSideModalConfigs);
        const type = nodeName?.toLowerCase() === "bik ai" ? "AI" : nodeName;
        setNewNodeData({ type });
        setShowRightModal(true);
      }
    }
  };

  const setNodeDataFromSideModal = (
    blockId: string,
    blockName: string,
    blockChannel?: string
  ) => {
    const id = getNodeId();
    const index = (nodeSubtypeMapping.data?.[blockId] || 0) + 1;
    setNewNodeData((prevState: { [key: string]: string | number }) => {
      return {
        ...prevState,
        subType: blockId,
        nodeIndex: index,
        title: `${blockName} #${index}`,
        automationChannel: blockChannel || "",
        id: id,
      };
    });
    setShowRightModal((prevState: boolean) => !prevState);
  };

  const showNotification = (notificationDataState: NotificationDataState) => {
    if (notificationDataState.successMessage) {
      setToasterString(notificationDataState.successMessage);
      setToasterType("success");
      setShowToaster(true);
    } else if (notificationDataState.errorMessage) {
      setToasterString(notificationDataState.errorMessage);
      setToasterType("error");
      setShowToaster(true);
    } else if (notificationDataState.warningMessage) {
      setToasterString(notificationDataState.warningMessage);
      setToasterType("warning");
      setShowToaster(true);
    }
  };

  const getNodeId = (): string => {
    let ix = index + 1;
    setIndex(ix);
    return `N${ix}`;
  };

  const getIsBikIntegrated = async (bikStore: BikStore): Promise<boolean> => {
    const partnerDomainUrl =
      bikStore?.storeIntegrationStatus?.partnerStoreDomain;
    const bikStoreId = await firebaseService.isBikIntegratedAlready(
      bikStore.storeId,
      partnerDomainUrl
    );

    if (!bikStoreId || typeof bikStoreId !== "string") {
      return false;
    }
    return true;
  };

  const resetRedux = () => {
    dispatcher({ type: "reset" });
    setIndex((prevIndex: number) => 1);
  };

  useEffect(() => {
    return () => {
      resetRedux();
    };
  }, []);

  useEffect(() => {
    if (!homeState.userData || !homeState.userData.uid) {
      return;
    }
    if (homeState.userData && homeState.userData?.loginType === "sso") {
      setRunTour(false);
      return;
    }
    if (!props.showTour || prevTourRef.current === props.showTour) {
      return;
    }

    prevTourRef.current = props.showTour;
    setRunTour(false);
    setResetPort((prev) => prev + 1);
    const isAnalyticsPath =
      location.pathname.endsWith("/analytics") ||
      location.pathname.includes("/analytics/login/");
    if (isAnalyticsPath) {
      setTimeout(() => {
        setSteps(tourStepsAnalytics);
        setRunTour(true);
      }, 1000);
    } else {
      setTimeout(() => {
        setSteps(tourSteps);
        setRunTour(true);
      }, 1000);
    }
  }, [props.showTour, homeState.userData]);

  useEffect(() => {
    if (!homeState.userData || !homeState.userData.uid) {
      return;
    }
    if (homeState.userData && homeState.userData?.loginType === "sso") {
      setRunTour(false);
      return;
    }
    setRunTour(false);
    const isAnalyticsPath =
      location.pathname.endsWith("/analytics") ||
      location.pathname.includes("/analytics/login/");

    if (isAnalyticsPath) {
      setShowRightModal(false);
      setShowLeftModal(false);
    }

    setTimeout(() => {
      const enabled = (function () {
        if (typeof localStorage === "undefined") {
          return false;
        }
        return !fetchPageTourStatus(
          isAnalyticsPath ? PAGES.NLA_ANALYTICS : PAGES.JOURNEY_BUILDER_HOME
        );
      })();
      if (!enabled) {
        return;
      }
      if (isAnalyticsPath) {
        setResetPort((prev) => prev + 1);
        setSteps(tourStepsAnalytics);
        setRunTour(true);
      } else {
        setResetPort((prev) => prev + 1);
        setSteps(tourSteps);
        setRunTour(true);
      }
    }, 3000);
  }, [location, homeState.userData]);

  return (
    <>
      {showToaster && (
        <Toaster
          text={toasterString}
          type={toasterType}
          autoClose={3000}
          onCloseToast={() => {
            setShowToaster(false);
          }}
          width={"300px"}
        />
      )}
      <ReactJoyride
        steps={steps}
        disableScrolling={false}
        disableScrollParentFix={false}
        run={runTour}
        scrollToFirstStep={true}
        tooltipComponent={TourTooltip}
        continuous
        showSkipButton
        callback={(data) => {
          if (
            ([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(
              data.status
            ) ||
            ["skip", "reset", "close"].includes(data.action)
          ) {
            const isAnalyticsPath =
              location.pathname.endsWith("/analytics") ||
              location.pathname.includes("/analytics/login/");
            const currentUser = JSON.parse(
              localStorage.getItem("journey_login_uid") || "{}"
            );
            if (currentUser) {
              updatePageTourStatus(
                storeId!,
                currentUser,
                isAnalyticsPath
                  ? PAGES.NLA_ANALYTICS
                  : PAGES.JOURNEY_BUILDER_HOME
              ).then();
            }
          }
        }}
        styles={{
          buttonNext: {
            background: COLORS.background.brand,
          },
          tooltipContainer: {
            textAlign: "left",
          },
          options: {
            primaryColor: COLORS.content.brand,
            spotlightShadow: "0px 0px 14px 0px rgba(255, 255, 255, 0.90)",
          },
        }}
      />
      <ChatbotBuilderStyled>
        <NodeProvider
          forceFetch={props?.forceFetch}
          newNodeData={newNodeData}
          storeId={storeId!}
          flowId={flowId!}
          type={type!}
          setIndex={(idx) => setIndex(idx)}
          index={index}
          getNodeId={getNodeId}
          resetViewPort={resetPort}
        />
        <div
          ref={superRef}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!isAnalytics && (
            <AddNodePopup
              click={addNewNode}
              isActive={showRightModal}
              activeNode={newNodeData?.type?.toString()}
              channel={channel as string}
            />
          )}

          {sideModalConfigs[newNodeData?.type]?.subSections &&
            !PreventSideBar[newNodeData?.subType] && (
              <SideModal
                isOpen={showRightModal}
                storeId={storeId!}
                header={sideModalConfigs[newNodeData?.type]?.header!}
                caption={sideModalConfigs[newNodeData?.type]?.caption!}
                subSections={sideModalConfigs[newNodeData?.type]?.subSections!}
                closeModal={rightModalToggler}
                setNewNodeData={setNodeDataFromSideModal}
                isBikIntegrated={isBikIntegrated}
                isManifestStore={isManifestStore}
                feature={normalizeFeatureBlockId(
                  searchParams.get("feature") ?? ""
                )}
              />
            )}
        </div>
        {homeState?.showVersionHistory &&
          homeState?.userData?.loginType === "sso" &&
          !isAnalytics && <VersionHistoryPanel />}
        {showLeftModal && !isAnalytics && (
          <ComponentModal
            storeId={storeId!}
            flowId={flowId!}
            config={config}
            closeModal={closeLeftModal}
          />
        )}
      </ChatbotBuilderStyled>
    </>
  );
};

const PreventSideBar: { [key: string]: boolean } = {
  Condition: true,
  add_tags: true,
  start_flow: true,
};

export default ChatbotBuilder;
